import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, AlertTriangle } from 'lucide-react';
import { clearAuth, getToken } from '@/utils/auth';
import api from '@/utils/api';

/**
 * IdleLogoutManager — enforces the SAFER-i™ security session policy.
 *
 *  1. **10-min idle logout**: any mouse / keyboard / scroll / touch activity
 *     resets the timer. At 9:30, a confirmation modal appears with a 30-second
 *     countdown. If the user clicks "Stay logged in" it resets to 10 min;
 *     otherwise we clear auth and redirect to /login.
 *  2. **Sliding token refresh**: while the user is active we silently call
 *     `POST /api/auth/refresh` every 50 minutes so the 60-min JWT never expires
 *     mid-session.
 *  3. **Tab-hidden screen-lock alert**: when the page becomes hidden (tab
 *     switched, window minimized, OS screen-lock) for >= 2 minutes, we POST
 *     /api/auth/screen-lock-alert which emails admin@safer-i.com.
 *  4. **Idle logout email**: same endpoint with event_type='idle_logout' fires
 *     just before we clear the session.
 *  5. **Trusted device opt-in**: the modal also shows a "Trust this device for
 *     24h" button which calls `POST /api/auth/trust-device`, swaps the
 *     short-lived token for a 24-hour token, and DISABLES the idle / hidden
 *     timers for the duration. The token is stored in localStorage so it
 *     survives a browser close.
 *
 * Mounts at the App level inside <ProtectedRoute> children. No-ops when the
 * user is unauthenticated or when the current device is in trusted-window.
 */

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;          // 10 minutes
const WARNING_LEAD_MS = 30 * 1000;                // show modal 30s before logout
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;       // refresh JWT every 50 min
const HIDDEN_ALERT_THRESHOLD_MS = 2 * 60 * 1000;  // alert if hidden ≥ 2 min
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const TRUST_TOKEN_KEY = 'safer_trusted_token';
const TRUST_UNTIL_KEY = 'safer_trusted_until';

export default function IdleLogoutManager() {
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const hiddenSinceRef = useRef(null);
  const alertFiredRef = useRef(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_LEAD_MS / 1000));
  const [trusting, setTrusting] = useState(false);
  const countdownRef = useRef(null);

  const isAuthed = useCallback(() => !!getToken(), []);

  /** Returns true if a non-expired trusted-device token is in localStorage.
   *  When trusted, we skip idle warnings, tab-hidden alerts, AND the sliding
   *  refresh (the trusted token already lives 24h). */
  const isTrustedDevice = useCallback(() => {
    try {
      const until = localStorage.getItem(TRUST_UNTIL_KEY);
      if (!until) return false;
      return Date.now() < new Date(until).getTime();
    } catch {
      return false;
    }
  }, []);

  /** On mount, if a valid trusted token is sitting in localStorage but
   *  sessionStorage is empty (browser closed + reopened), restore it so the
   *  user stays logged in inside the trust window. */
  useEffect(() => {
    try {
      if (isTrustedDevice() && !sessionStorage.getItem('safer_token')) {
        const stashed = localStorage.getItem(TRUST_TOKEN_KEY);
        if (stashed) sessionStorage.setItem('safer_token', stashed);
      } else if (!isTrustedDevice()) {
        // Trust expired — clean up
        localStorage.removeItem(TRUST_TOKEN_KEY);
        localStorage.removeItem(TRUST_UNTIL_KEY);
      }
    } catch { /* noop */ }
  }, [isTrustedDevice]);

  /** Clear auth + email admin + redirect. Idempotent. */
  const performLogout = useCallback(async (eventType) => {
    if (!isAuthed()) return;
    try {
      await api.post('/auth/screen-lock-alert', {
        event_type: eventType,
        last_active_at: new Date(lastActivityRef.current).toISOString(),
      });
    } catch {
      /* swallow — we're logging out regardless */
    }
    // Drop the trusted-device token too — explicit logout means no trust.
    try {
      localStorage.removeItem(TRUST_TOKEN_KEY);
      localStorage.removeItem(TRUST_UNTIL_KEY);
    } catch { /* noop */ }
    clearAuth();
    setWarningOpen(false);
    if (window.location.pathname !== '/login') {
      navigate('/login?reason=idle');
    }
  }, [isAuthed, navigate]);

  /** Cancel pending timers (called when activity arrives or unmount). */
  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  /** Schedule the next warning + final logout based on lastActivity. */
  const scheduleIdleTimers = useCallback(() => {
    clearTimers();
    warningTimerRef.current = setTimeout(() => {
      // Show the modal and start the countdown
      setSecondsLeft(Math.floor(WARNING_LEAD_MS / 1000));
      setWarningOpen(true);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_LEAD_MS);

    idleTimerRef.current = setTimeout(() => {
      performLogout('idle_logout');
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, performLogout]);

  /** Recorded user activity → reset all idle timers. */
  const onActivity = useCallback(() => {
    if (!isAuthed()) return;
    lastActivityRef.current = Date.now();
    if (warningOpen) setWarningOpen(false);
    scheduleIdleTimers();
  }, [isAuthed, scheduleIdleTimers, warningOpen]);

  // Wire up activity listeners + initial schedule
  useEffect(() => {
    if (!isAuthed() || isTrustedDevice()) return;
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    scheduleIdleTimers();
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      clearTimers();
    };
  }, [isAuthed, isTrustedDevice, onActivity, scheduleIdleTimers, clearTimers]);

  // Sliding token refresh — silent. Skipped on trusted devices because the
  // trust token already lives 24h.
  useEffect(() => {
    if (!isAuthed() || isTrustedDevice()) return;
    refreshTimerRef.current = setInterval(async () => {
      if (!isAuthed()) return;
      try {
        const res = await api.post('/auth/refresh');
        if (res?.data?.token) {
          sessionStorage.setItem('safer_token', res.data.token);
        }
      } catch {
        /* If refresh fails (e.g. server-issued logout), the next API call
           will 401 and the api.js interceptor will redirect to /login. */
      }
    }, REFRESH_INTERVAL_MS);
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
  }, [isAuthed, isTrustedDevice]);

  // Tab visibility — fire screen-lock alert if hidden ≥ 2 min. Skipped on
  // trusted devices.
  useEffect(() => {
    if (!isAuthed() || isTrustedDevice()) return;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        alertFiredRef.current = false;
      } else if (hiddenSinceRef.current) {
        const hiddenMs = Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
        if (hiddenMs >= HIDDEN_ALERT_THRESHOLD_MS && !alertFiredRef.current) {
          alertFiredRef.current = true;
          api.post('/auth/screen-lock-alert', {
            event_type: 'tab_hidden_2min',
            last_active_at: new Date(lastActivityRef.current).toISOString(),
          }).catch(() => { /* swallow */ });
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthed, isTrustedDevice]);

  // When the countdown hits 0 inside the warning modal, force logout.
  useEffect(() => {
    if (warningOpen && secondsLeft === 0) {
      performLogout('idle_logout');
    }
  }, [warningOpen, secondsLeft, performLogout]);

  /** "Trust this device for 24h" — swap the short token for a long-lived one
   *  and stash it in localStorage so it survives browser close. Idle warnings
   *  + tab-hidden alerts disable themselves while the trust window is open. */
  const handleTrustDevice = useCallback(async () => {
    setTrusting(true);
    try {
      const res = await api.post('/auth/trust-device');
      if (res?.data?.token) {
        sessionStorage.setItem('safer_token', res.data.token);
        try {
          localStorage.setItem(TRUST_TOKEN_KEY, res.data.token);
          if (res.data.trusted_until) {
            localStorage.setItem(TRUST_UNTIL_KEY, res.data.trusted_until);
          }
        } catch { /* localStorage may be blocked; trust still applies for this session */ }
      }
      setWarningOpen(false);
      clearTimers();
    } catch {
      // Fall back to keeping the user logged in for the current session
      setWarningOpen(false);
      onActivity();
    } finally {
      setTrusting(false);
    }
  }, [clearTimers, onActivity]);

  if (!isAuthed()) return null;

  return (
    <Dialog open={warningOpen} onOpenChange={(o) => { if (!o) onActivity(); }}>
      <DialogContent className="max-w-md" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            You're about to be logged out
          </DialogTitle>
          <DialogDescription>
            For your security, we sign you out after 10 minutes of inactivity.
            You'll be logged out in <span className="font-bold text-amber-700 tabular-nums" data-testid="idle-countdown">{secondsLeft}</span> seconds.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" />
          A security email will be sent to admin@safer-i.com if you let this expire.
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={onActivity}
            className="w-full bg-violet-600 hover:bg-violet-700"
            data-testid="idle-stay-logged-in-btn"
          >
            Stay logged in
          </Button>
          <Button
            variant="outline"
            onClick={handleTrustDevice}
            disabled={trusting}
            className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid="idle-trust-device-btn"
            title="Issues a 24-hour trusted-device token. Idle warnings won't appear on this device for the next day."
          >
            {trusting ? 'Trusting…' : 'Trust this device for 24 hours'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => performLogout('manual')}
            className="w-full text-slate-500"
            data-testid="idle-logout-now-btn"
          >
            Log me out now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
